<template>
  <ReportPageLayout>
    <template #page-header>
      <OverviewNavHeader
        v-if="report && report.report_grades && report.report_grades.length > 0"
        :report="report"
      />
    </template>

    <!-- Default slot for main content -->
    <div
      v-if="combinedLoading"
      class="d-flex justify-center align-center py-12"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      ></v-progress-circular>
    </div>
    <div
      v-else-if="combinedError && !report && !gatewayReport"
      class="error-container text-center py-8"
    >
      <v-alert
        type="error"
        title="Error Loading Report"
        :text="combinedError"
        class="mx-auto"
        max-width="600"
        variant="tonal"
        border="start"
      ></v-alert>
      <div class="mt-4">
        <button
          type="button"
          class="btn btn-primary me-3"
          @click="reloadReport"
        >
          Retry
        </button>
        <button
          type="button"
          class="btn btn-outline-secondary"
          @click="goToReportOverview"
        >
          Back to Report Overview
        </button>
      </div>
    </div>
    <div
      v-else-if="report && !gatewayReport && gatewayError"
      class="error-container text-center py-8"
    >
      <v-alert
        type="warning"
        title="Gateway Data Unavailable"
        :text="`The gateway data for ${route.params.gateway} is not available. This may be because the data hasn't been published yet or there was an issue loading it.`"
        class="mx-auto"
        max-width="600"
        variant="tonal"
        border="start"
      ></v-alert>
      <div class="mt-4">
        <button
          type="button"
          class="btn btn-primary me-3"
          @click="reloadGatewayReport"
        >
          Retry Gateway
        </button>
        <button
          type="button"
          class="btn btn-outline-secondary"
          @click="goToGradeOverview"
        >
          Back to Grade Overview
        </button>
      </div>
    </div>
    <v-responsive v-else-if="report" max-width="1320" class="mx-auto">
      <div id="top"></div>
      <v-row>
        <v-col cols="12">
          <GatewayHeadingCard
            :report="report"
            :selected-grade="String(route.params.grade)"
            :gateway-report="gatewayReport"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" md="3">
          <ReportSideNav
            :key="`${route.params.id}-${route.params.grade}-${route.params.gateway}`"
            :report="report"
            mode="gateway"
            :grade="route.params.grade as string"
            :currentGatewayReport="gatewayReport"
            :gradeReport="gradeReport"
          />
        </v-col>
        <v-col cols="12" md="9">
          <div>
            <template v-if="!isMllGatewayView">
              <SeriesPerformanceSnapshot
                :report="report"
                title="Ratings Snapshot"
                class="mb-8"
                id="ratings-snapshot"
              />

              <div id="gateway-ratings-summary">
                <GradeLevelSummaryRow
                  v-if="gradeReport"
                  :report="gradeReport"
                  :selected-grade="String(route.params.grade)"
                />
              </div>
            </template>

            <div id="grade-level-overview">
              <h4 class="font-weight-bold text-secondary mb-4">
                {{ gatewayTitle }}
              </h4>
              <h2 class="font-weight-bold text-secondary">
                {{ gatewayDescription }}
              </h2>

              <DataTable
                :headers="['score']"
                :hideHeader="true"
                :items="qualityAndComplexity"
                :skillName="true"
                :solidBg="true"
                classes="mt-6 mb-8"
              />

              <v-expand-transition>
                <div>
                  <ExpandedContent>
                    <div
                      v-if="gatewayReport"
                      v-katex:auto="{ options: katexOptions }"
                    >
                      <div
                        v-html="
                          renderHtmlContent(
                            gatewayReport.details || gatewayReport.description,
                          )
                        "
                      ></div>
                    </div>
                    <div v-else>Loading gateway details...</div>
                  </ExpandedContent>
                </div>
              </v-expand-transition>
            </div>

            <!-- Publication Details -->
            <div
              id="publication-details"
              v-if="report?.details && report.details.length > 0"
            >
              <PublicationDetails :report="report" :gradeReport="gradeReport" />
            </div>

            <v-divider class="my-12"></v-divider>

            <template
              v-for="(criterion, criterionIndex) in criteriaData"
              :key="criterion.label"
            >
              <IndicatorHeading :id="generateElementId('criterion', criterion)">
                <template #title>{{
                  getCriterionDisplayTitle(criterion)
                }}</template>
                <template #score>
                  <span
                    :class="
                      getScoreStatusColor(
                        criterion.rating?.score || 0,
                        criterion.rating?.total || 0,
                        criterion.rating?.review_status?.slug,
                      )
                    "
                    v-html="
                      formatScoreWithHtml(
                        criterion.rating?.score || 0,
                        criterion.rating?.total || 0,
                        criterion.rating?.review_status?.slug,
                        criterion.rating?.review_status?.title,
                      )
                    "
                  >
                  </span>
                </template>
                <template #description>
                  <!-- MLL Notice for Criteria containing MLL indicators -->
                  <div v-if="criterion.contains_mll" class="mll-notice mb-4">
                    <strong
                      >Information on Multilingual Learner (MLL) Supports in
                      This Criterion</strong
                    >
                    <p>
                      For some indicators in this criterion, we also display
                      evidence and scores for pair MLL indicators.
                      <a href="#" @click.prevent="openGlossary('mll')"
                        >Learn more</a
                      >
                    </p>
                    <p>
                      While MLL indicators are scored, these scores are reported
                      separately from core content scores. MLL scores do not
                      currently impact core content scores at any level—whether
                      indicator, criterion, gateway, or series.
                    </p>
                    <p>
                      To view all MLL evidence and scores for this grade band or
                      grade level, select the “Multilingual Learner Supports”
                      view from the left navigation panel.
                    </p>
                    <p>
                      <a href="#" @click.prevent="openGlossary('mll')">
                        Learn more about MLL supports, evidence, scores, and
                        pair indicators
                      </a>
                    </p>
                  </div>
                  <!-- Non-negotiable warning text -->
                  <div
                    v-if="isNonNegotiableCriterion(criterion)"
                    class="non-negotiable-warning mb-3"
                  >
                    <p>
                      <strong>This criterion is non-negotiable.</strong>
                      Materials must achieve a specified minimum score in this
                      criterion to advance to the next gateway.
                    </p>
                  </div>
                  <div v-katex:auto="{ options: katexOptions }">
                    <div
                      v-html="renderHtmlContent(criterion.description)"
                      class="criterion-description"
                    ></div>
                  </div>
                </template>
              </IndicatorHeading>

              <IndicatorBlock
                v-if="criterion.details"
                :color="
                  getStatusColor(criterion.rating?.review_status?.slug).replace(
                    'text-status-',
                    '',
                  )
                "
              >
                <div v-katex:auto="{ options: katexOptions }">
                  <div
                    v-html="renderHtmlContent(criterion.details)"
                    class="criterion-details"
                  ></div>
                </div>
              </IndicatorBlock>

              <IndicatorBlock
                v-if="criterion.supports && criterion.supports.length > 0"
                class="mb-8"
              >
                <div class="block-title">
                  <div class="title-text text-secondary">
                    {{
                      criterion.supports[0].type?.label || 'Additional Supports'
                    }}
                  </div>
                  <div
                    class="score-text"
                    :class="
                      getScoreStatusColor(
                        criterion.supports[0].criterion?.rating?.score || 0,
                        criterion.supports[0].criterion?.rating?.total || 0,
                        criterion.supports[0].criterion?.rating?.review_status
                          ?.slug,
                      )
                    "
                    v-html="
                      formatScoreWithHtml(
                        criterion.supports[0].criterion?.rating?.score || 0,
                        criterion.supports[0].criterion?.rating?.total || 0,
                        criterion.supports[0].criterion?.rating?.review_status
                          ?.slug,
                        criterion.supports[0].criterion?.rating?.review_status
                          ?.title,
                      )
                    "
                  ></div>
                </div>
                <div v-katex:auto="{ options: katexOptions }">
                  <div
                    v-html="
                      renderHtmlContent(
                        criterion.supports[0].criterion?.details ||
                          criterion.supports[0].criterion?.description,
                      )
                    "
                  ></div>
                </div>
              </IndicatorBlock>

              <template
                v-if="criterion.indicators && criterion.indicators.length > 0"
              >
                <IndicatorHeading
                  v-for="(indicator, indicatorIndex) in criterion.indicators"
                  :key="`${criterion.label}-${indicator.label}`"
                  :href="`#${generateElementId('indicator-detail', criterion, indicator)}`"
                  :linked-title="true"
                  :hide-border="
                    indicatorIndex === criterion.indicators.length - 1
                  "
                  @open-glossary="scrollToIndicatorDetail(criterion, indicator)"
                >
                  <template #title>{{ indicator.label }}</template>
                  <template
                    #sub-title
                    v-if="indicator.supports && indicator.supports.length > 0"
                  >
                    {{ indicator.supports[0].type?.label || 'Supports' }}
                    <span
                      class="sub-title-score"
                      :class="
                        isMainGatewayMllIndicator(indicator)
                          ? 'text-meta-grey'
                          : getScoreStatusColor(
                              indicator.supports[0].indicator?.rating?.score ||
                                0,
                              indicator.supports[0].indicator?.rating?.total ||
                                0,
                              indicator.supports[0].indicator?.rating
                                ?.review_status?.slug,
                            )
                      "
                      v-html="
                        isMainGatewayMllIndicator(indicator)
                          ? formatScoreWithHtml(
                              indicator.supports[0].indicator?.rating?.score ||
                                0,
                              indicator.supports[0].indicator?.rating?.total ||
                                0,
                              undefined,
                              undefined,
                              true,
                            )
                          : formatScoreWithHtml(
                              indicator.supports[0].indicator?.rating?.score ||
                                0,
                              indicator.supports[0].indicator?.rating?.total ||
                                0,
                              indicator.supports[0].indicator?.rating
                                ?.review_status?.slug,
                              indicator.supports[0].indicator?.rating
                                ?.review_status?.title,
                            )
                      "
                    >
                    </span>
                  </template>
                  <template #score>
                    <span
                      :class="
                        isMainGatewayMllIndicator(indicator)
                          ? 'text-meta-grey'
                          : getScoreStatusColor(
                              indicator.rating?.score || 0,
                              indicator.rating?.total || 0,
                              indicator.rating?.review_status?.slug,
                            )
                      "
                      v-html="
                        isMainGatewayMllIndicator(indicator)
                          ? formatScoreWithHtml(
                              indicator.rating?.score || 0,
                              indicator.rating?.total || 0,
                              undefined,
                              undefined,
                              true,
                            )
                          : formatScoreWithHtml(
                              indicator.rating?.score || 0,
                              indicator.rating?.total || 0,
                              indicator.rating?.review_status?.slug,
                              indicator.rating?.review_status?.title,
                            )
                      "
                    >
                    </span>
                  </template>
                </IndicatorHeading>
              </template>

              <!-- Detailed indicator sections nested under this criterion -->
              <template
                v-if="criterion.indicators && criterion.indicators.length > 0"
              >
                <template
                  v-for="(indicator, indicatorIndex) in criterion.indicators"
                  :key="`detail-${criterion.label}-${indicator.label}`"
                >
                  <v-divider class="my-12"></v-divider>

                  <IndicatorHeading
                    :id="
                      generateElementId(
                        'indicator-detail',
                        criterion,
                        indicator,
                      )
                    "
                  >
                    <template #title>{{ indicator.label }}</template>
                    <template #score>
                      <span
                        :class="
                          isMainGatewayMllIndicator(indicator)
                            ? 'text-meta-grey'
                            : getScoreStatusColor(
                                indicator.rating?.score || 0,
                                indicator.rating?.total || 0,
                                indicator.rating?.review_status?.slug,
                              )
                        "
                        v-html="
                          isMainGatewayMllIndicator(indicator)
                            ? formatScoreWithHtml(
                                indicator.rating?.score || 0,
                                indicator.rating?.total || 0,
                                undefined,
                                undefined,
                                true,
                              )
                            : formatScoreWithHtml(
                                indicator.rating?.score || 0,
                                indicator.rating?.total || 0,
                                indicator.rating?.review_status?.slug,
                                indicator.rating?.review_status?.title,
                              )
                        "
                      >
                      </span>
                    </template>
                    <template #description>
                      <div v-katex:auto="{ options: katexOptions }">
                        <div
                          v-html="renderHtmlContent(indicator.description)"
                          class="indicator-description"
                        ></div>
                      </div>
                    </template>
                  </IndicatorHeading>

                  <!-- Claims Component Integration - Temporarily hidden while backend data is updated -->
                  <!-- <ShowClaims
                    v-if="getClaimsForIndicator(indicator).length > 0"
                    :title="`Indicator ${indicator.label} Claims`"
                    :claims="getClaimsForIndicator(indicator)"
                    :subheader="true"
                    class="mb-6"
                  /> -->

                  <IndicatorBlock
                    v-if="indicator.details"
                    :color="
                      isMainGatewayMllIndicator(indicator)
                        ? 'grey'
                        : getStatusColor(
                            indicator.rating?.review_status?.slug,
                          ).replace('text-status-', '')
                    "
                  >
                    <div v-katex:auto="{ options: katexOptions }">
                      <div
                        v-html="renderHtmlContent(indicator.details)"
                        class="indicator-details"
                      ></div>
                    </div>
                  </IndicatorBlock>

                  <IndicatorBlock
                    v-if="indicator.supports && indicator.supports.length > 0"
                    class="mb-8"
                  >
                    <div class="block-title">
                      <div class="title-text text-secondary">
                        {{
                          indicator.supports[0].type?.label ||
                          'Additional Supports'
                        }}
                      </div>
                      <div
                        class="score-text"
                        :class="
                          getScoreStatusColor(
                            indicator.supports[0].indicator?.rating?.score || 0,
                            indicator.supports[0].indicator?.rating?.total || 0,
                            indicator.supports[0].indicator?.rating
                              ?.review_status?.slug,
                          )
                        "
                        v-html="
                          formatScoreWithHtml(
                            indicator.supports[0].indicator?.rating?.score || 0,
                            indicator.supports[0].indicator?.rating?.total || 0,
                            indicator.supports[0].indicator?.rating
                              ?.review_status?.slug,
                            indicator.supports[0].indicator?.rating
                              ?.review_status?.title,
                          )
                        "
                      ></div>
                    </div>
                    <div v-katex:auto="{ options: katexOptions }">
                      <div
                        v-html="
                          renderHtmlContent(
                            indicator.supports[0].indicator?.details ||
                              indicator.supports[0].indicator?.description,
                          )
                        "
                      ></div>
                    </div>
                  </IndicatorBlock>
                </template>
              </template>

              <v-divider
                v-if="criterionIndex < criteriaData.length - 1"
                class="my-12"
              ></v-divider>
            </template>

            <div
              v-if="!gatewayLoading && criteriaData.length === 0"
              class="text-center py-8"
            >
              <v-alert
                type="info"
                title="No Criteria Data"
                text="No detailed criteria information is available for this gateway."
                class="mx-auto"
                max-width="600"
              ></v-alert>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-responsive>
    <div v-else class="text-center py-8">
      <v-alert
        type="info"
        title="No Report Data"
        text="No grade overview data was found."
        class="mx-auto"
        max-width="600"
      ></v-alert>
    </div>
  </ReportPageLayout>
</template>

<script setup lang="ts">
  import { computed, inject, ref } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useGlossary } from '../composables/useGlossary';
  import {
    useReportLoader,
    useGatewayReportLoader,
    useGradeReportLoader,
  } from '../composables/useReportLoader';
  import { useDocumentTitle } from '../composables/useDocumentTitle';
  import type { Report } from '../store';

  import ReportPageLayout from '../layouts/ReportPageLayout.vue';
  import Glossary from '../components/glossary/Glossary.vue';
  import OverviewNavHeader from '../components/reportOverview/OverviewNavHeader.vue';
  import ReportSideNav from '../components/shared/ReportSideNav.vue';
  import GatewayHeadingCard from '../components/reportGateway/GatewayHeadingCard.vue';
  import ExpandedContent from '../components/shared/ExpandedContent.vue';
  import DataTable from '../components/shared/DataTable.vue';
  import GradeLevelSummaryRow from '../components/reportGrade/GradeOverviewSummaryRow.vue';
  import SeriesPerformanceSnapshot from '../components/reports/SeriesPerformanceSnapshot.vue';
  import IndicatorBlock from '../components/shared/IndicatorBlock.vue';
  import IndicatorHeading from '../components/shared/IndicatorHeading.vue';
  import PublicationDetails from '../components/reportGrade/PublicationDetails.vue';
  import { ShowClaims } from '../components/claims';

  // Import math rendering utility
  import { renderMathInHtml } from '../utils/mathRenderer';

  const route = useRoute();
  const router = useRouter();
  const { openGlossary: openGlossaryComposable } = useGlossary();

  const gradeLevel = computed(() => {
    const grade = route.params.grade;
    if (typeof grade !== 'string') return 'K';
    if (grade === 'kindergarten') return 'K';
    const gradeNumber = grade.split('-')[0];
    return gradeNumber;
  });

  const reportSlug = computed(() => route.params.id as string);
  const { report, loading, error, reloadReport } = useReportLoader(reportSlug);

  const gradeParam = computed(() => route.params.grade as string);
  const gatewayParam = computed(() => route.params.gateway as string);

  // Load grade-specific data for navigation
  const {
    gradeReport,
    loading: gradeLoading,
    error: gradeError,
  } = useGradeReportLoader(reportSlug, gradeParam);

  const {
    gatewayReport,
    loading: gatewayLoading,
    error: gatewayError,
    reloadGatewayReport,
  } = useGatewayReportLoader(reportSlug, gradeParam, gatewayParam);

  const combinedLoading = computed(
    () => loading.value || gatewayLoading.value || gradeLoading.value,
  );
  const combinedError = computed(
    () => error.value || gatewayError.value || gradeError.value,
  );

  const katexOptions = {
    ignoredClasses: [],
    preProcess: (math: Text) => {
      const parentElement = math.parentElement;
      if (
        parentElement &&
        parentElement.tagName === 'SPAN' &&
        parentElement.classList.contains('katex')
      ) {
        return math.textContent;
      }

      return '';
    },
  };

  // Helper function to decode HTML entities
  function decodeHtmlEntities(text: string | null | undefined): string {
    if (!text) return '';
    if (typeof document !== 'undefined') {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      return textarea.value;
    }
    return text
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&#37;/g, '%')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ');
  }

  // Helper function to format scores - show "Narrative Only" when review_status.slug is 'narrative-only'
  function formatScore(
    score: number,
    total: number,
    reviewStatusSlug?: string,
    reviewStatusTitle?: string,
  ): string {
    if (reviewStatusSlug === 'narrative-only') {
      return reviewStatusTitle || 'Narrative Only';
    }
    if (total === 0) {
      return 'Narrative Only';
    }
    return `${score} / ${total}`;
  }

  // Helper function to format scores with HTML formatting
  function formatScoreWithHtml(
    score: number,
    total: number,
    reviewStatusSlug?: string,
    reviewStatusTitle?: string,
    boldScore: boolean = true,
  ): string {
    if (reviewStatusSlug === 'narrative-only') {
      return reviewStatusTitle || 'Narrative Only';
    }
    if (total === 0) {
      return 'Narrative Only';
    }
    if (boldScore) {
      return `<span class="font-weight-bold">${score}</span> / ${total}`;
    }
    return `${score} / ${total}`;
  }

  function renderHtmlContent(text: string | null | undefined): string {
    if (!text) return '';
    const decoded = decodeHtmlEntities(text);

    let htmlContent: string;
    if (decoded.includes('<') && decoded.includes('>')) {
      htmlContent = decoded;
    } else {
      htmlContent = `<p>${decoded}</p>`;
    }

    // Process math equations using our utility
    return renderMathInHtml(htmlContent);
  }

  function getStatusColor(statusSlug: string | undefined): string {
    if (!statusSlug) return 'text-meta-grey';
    const colorMap: Record<string, string> = {
      meets: 'text-status-green',
      'partially-meets': 'text-status-orange',
      'does-not-meet': 'text-status-red',
      'did-not-review': 'text-meta-grey',
      'not-eligible': 'text-meta-grey',
      'does-not-exist': 'text-meta-grey',
      'no-status': 'text-meta-grey',
      'narrative-only': 'text-meta-grey',
      'zero-score': 'text-status-red',
    };
    return colorMap[statusSlug] || 'text-meta-grey';
  }

  function getScoreStatusSlug(
    score: number,
    total: number,
    reviewStatusSlug?: string,
  ): string {
    if (reviewStatusSlug === 'narrative-only') {
      return 'narrative-only';
    }
    if (total === 0) {
      return 'narrative-only'; // Use neutral color for narrative-only
    }
    if (score === 0 && total === 0) {
      return 'zero-score';
    }
    return reviewStatusSlug || 'text-meta-grey';
  }

  // Helper function to get appropriate status color, defaulting to neutral for narrative-only
  function getScoreStatusColor(
    score: number,
    total: number,
    reviewStatusSlug?: string,
  ): string {
    if (reviewStatusSlug === 'narrative-only') {
      return 'text-meta-grey'; // Use neutral color for narrative-only
    }
    if (total === 0) {
      return 'text-meta-grey'; // Use neutral color for narrative-only
    }
    return getStatusColor(getScoreStatusSlug(score, total, reviewStatusSlug));
  }

  const qualityAndComplexity = computed(() => {
    if (!gatewayReport.value) return [];

    const gateway = gatewayReport.value;
    const items = [];

    const gatewayStatusTitle = decodeHtmlEntities(
      gateway.review_status?.title || 'No Status',
    );
    const gatewayStatusSlug = gateway.review_status?.slug || 'no-status';
    const gatewayScore = gateway.score || 'N/A';
    const gatewayLabel = decodeHtmlEntities(gateway.label || 'Gateway');

    // Check if we're in the MLL gateway view
    const isMllGatewayView = [
      'gateway-four',
      'multilingual-learner-supports',
    ].includes(gatewayParam.value);

    // Filter out gateway-level "Multilingual Learner Supports" entries only in MLL gateway view
    const shouldFilterGateway =
      isMllGatewayView &&
      gatewayLabel.toLowerCase().includes('multilingual learner supports');

    if (!shouldFilterGateway) {
      items.push({
        skill: `<span class="text-secondary">${gatewayLabel} -</span> <span class="${getStatusColor(gatewayStatusSlug)}">${gatewayStatusTitle}</span>`,
        score: `<h1 class="${getStatusColor(gatewayStatusSlug)}">${decodeHtmlEntities(gatewayScore)}</h1>`,
        alignments: {
          score: 'text-right',
        },
      });
    }

    if (gateway.criterion && Array.isArray(gateway.criterion)) {
      gateway.criterion.forEach((criterion: any) => {
        const supportStatusSlug =
          criterion.supports?.[0]?.criterion?.rating?.review_status?.slug;
        const supportScore =
          criterion.supports?.[0]?.criterion?.rating?.score || 0;
        const supportTotal =
          criterion.supports?.[0]?.criterion?.rating?.total || 0;
        const supportText =
          criterion.supports && criterion.supports.length > 0
            ? `${criterion.supports[0].type?.label || 'Supports'}: <span class="${getScoreStatusColor(supportScore, supportTotal, supportStatusSlug)}">${formatScoreWithHtml(supportScore, supportTotal)}</span>`
            : '';

        const criterionTitle = getCriterionDisplayTitle(criterion);

        // Filter out "Multilingual Learner Supports" entries only in MLL gateway view
        const shouldFilterCriterion =
          isMllGatewayView &&
          criterionTitle &&
          criterionTitle
            .toLowerCase()
            .includes('multilingual learner supports');

        if (shouldFilterCriterion) {
          return; // Skip this item
        }

        items.push({
          skill: `<span class="text-secondary">${decodeHtmlEntities(criterionTitle)}</span>`,
          subtitle: supportText,
          score: `<span class="${getScoreStatusColor(criterion.rating?.score || 0, criterion.rating?.total || 0, criterion.rating?.review_status?.slug)}">${formatScoreWithHtml(criterion.rating?.score || 0, criterion.rating?.total || 0)}</span>`,
          alignments: {
            score: 'text-right',
          },
        });
      });
    }

    return items;
  });

  const gatewayTitle = computed(() => {
    if (!gatewayReport.value) return 'Gateway Overview';
    return decodeHtmlEntities(
      gatewayReport.value.name ||
        gatewayReport.value.label ||
        'Gateway Overview',
    );
  });

  const gatewayDescription = computed(() => {
    if (!gatewayReport.value) return '';
    return decodeHtmlEntities(
      gatewayReport.value.title || gatewayReport.value.description || '',
    );
  });

  const criteriaData = computed(() => {
    if (!gatewayReport.value?.criterion) return [];

    const mapped = gatewayReport.value.criterion.map((criterion: any) => ({
      ...criterion,
      label: decodeHtmlEntities(criterion.label),
      name: decodeHtmlEntities(criterion.name),
      title: decodeHtmlEntities(criterion.title),
      description: criterion.description,
      details: criterion.details,
      indicators: criterion.indicators
        ? criterion.indicators.map((indicator: any) => {
            return {
              ...indicator,
              label: decodeHtmlEntities(indicator.label),
              description: indicator.description,
              details: indicator.details,
              claims: indicator.claims || [], // Ensure claims data is included
            };
          })
        : [],
    }));

    return mapped;
  });

  const gateway = computed(() => {
    const gw = route.params.gateway;
    return typeof gw === 'string' ? gw : '1';
  });

  const isMllGatewayView = computed(() => {
    return ['gateway-four', 'multilingual-learner-supports'].includes(
      gatewayParam.value,
    );
  });

  const documentTitleState = computed(() => {
    if (loading.value) return 'Loading Report...';
    if (error.value) return 'Error Loading Report';
    if (report.value?.title)
      return `${report.value.title} - Grade ${gradeLevel.value} - Gateway ${gateway.value}`;
    return 'Gateway Overview';
  });

  useDocumentTitle(documentTitleState);

  function generateElementId(
    type: string,
    criterion?: any,
    indicator?: any,
  ): string {
    const gatewaySlug = gatewayParam.value || 'gateway';

    if (type === 'criterion' && criterion) {
      return `${gatewaySlug}-${criterion.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'criterion'}`;
    } else if (
      (type === 'indicator' || type === 'indicator-detail') &&
      criterion &&
      indicator
    ) {
      const criterionSlug =
        criterion.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-') ||
        'criterion';
      const indicatorSlug =
        indicator.label?.toLowerCase().replace(/[^a-z0-9]+/g, '-') ||
        'indicator';
      return `${gatewaySlug}-${criterionSlug}-${indicatorSlug}`;
    }

    return `${gatewaySlug}-${type}`;
  }

  function scrollToIndicatorDetail(criterion: any, indicator: any) {
    const elementId = generateElementId(
      'indicator-detail',
      criterion,
      indicator,
    );
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  }

  // Helper function to get claims for an indicator (real or temporary)
  function getClaimsForIndicator(indicator: any): any[] {
    // Only use real claims data from the API
    if (
      indicator.claims &&
      Array.isArray(indicator.claims) &&
      indicator.claims.length > 0
    ) {
      return indicator.claims;
    }

    return [];
  }

  function isNonNegotiableCriterion(criterion: any): boolean {
    // Check if the criterion has explicit non_negotiable field
    if (
      criterion.non_negotiable === true ||
      String(criterion.non_negotiable) === 'Non-negotiable'
    ) {
      return true;
    }

    // Check for foundational skills that are typically non-negotiable
    const label = (criterion.label || '').toLowerCase();
    const name = (criterion.name || '').toLowerCase();
    const title = (criterion.title || '').toLowerCase();

    const nonNegotiablePatterns = [
      'phonemic awareness',
      'phonics',
      'alphabet knowledge',
      'foundational skills',
      'letter recognition',
      'sound-letter correspondence',
      'decoding',
      'systematic instruction',
      'explicit instruction',
    ];

    return nonNegotiablePatterns.some(
      (pattern) =>
        label.includes(pattern) ||
        name.includes(pattern) ||
        title.includes(pattern),
    );
  }

  // Helper function to get claims for an indicator (real or temporary)
  function getCriterionDisplayTitle(criterion: any): string {
    // If criterion has a more descriptive title field, use it
    if (criterion.title && criterion.title !== criterion.name) {
      return criterion.title;
    }

    // If we have detailed criterion data with unique names, use label + name
    // Otherwise, just use the label to avoid repetitive names like "Text Quality"
    if (criterion.name && criterion.label) {
      // Check if this appears to be repetitive (common gateway-level names)
      const repetitiveNames = [
        'Text Quality',
        'Building Knowledge',
        'Usability',
      ];
      if (repetitiveNames.includes(criterion.name.trim())) {
        return criterion.label; // Just use "Criterion 1.1", "Criterion 1.2", etc.
      }
      // If it's a more specific/unique name, combine them
      return `${criterion.label}: ${criterion.name}`;
    }

    // Fallback to just the label
    return criterion.label || 'Criterion';
  }

  function openGlossary(term: string) {
    openGlossaryComposable(term);
  }

  // Helper to determine if this is an MLL indicator in a main gateway (not in the MLL gateway view)
  function isMainGatewayMllIndicator(indicator: any): boolean {
    // Only apply gray coloring for MLL indicators in Gateways 1, 2, 3
    // Exclude MLL gateway (which may be 'mll', 'gateway-four', or 'multilingual-learner-supports')
    const mainGatewaySlugs = ['gateway-one', 'gateway-two', 'gateway-three'];

    // Check multiple possible MLL indicator properties and patterns
    const isMllIndicator =
      indicator.isMll === true ||
      indicator.is_mll === true ||
      indicator.mll === true ||
      (indicator.label &&
        (indicator.label.toLowerCase().includes('.mll') ||
          indicator.label.toLowerCase().includes('mll') ||
          indicator.label.toLowerCase().includes('multilingual'))) ||
      (indicator.slug && indicator.slug.toLowerCase().includes('mll')) ||
      (indicator.name && indicator.name.toLowerCase().includes('mll'));

    const result =
      isMllIndicator && mainGatewaySlugs.includes(gatewayParam.value);

    // MLL indicator check result
    return result;

    return result;
  }

  function goToReportOverview() {
    router.push({ name: 'report-overview', params: { id: reportSlug.value } });
  }

  function goToGradeOverview() {
    router.push({
      name: 'report-grade-overview',
      params: { id: reportSlug.value, grade: gradeParam.value },
    });
  }
</script>

<style lang="scss" scoped>
  .page-container {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  .page-content-container {
    flex: 1;
  }

  .grade-overview-section {
    padding: 24px;
    background-color: white;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  :deep(.text-meta-grey) {
    color: rgb(var(--v-theme-meta-grey)) !important;
  }

  // Global paragraph styling for consistent typography
  :deep(p) {
    font-size: 18px !important;
    line-height: 1.5;
    margin-top: 12px;
    margin-bottom: 16px;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  .non-negotiable-warning {
    margin-bottom: 16px;

    p {
      margin: 0;
      font-size: 18px;
      line-height: 1.4;
      color: rgb(var(--v-theme-on-surface));
    }
  }

  .mll-notice {
    background: #f5f5f5;
    border-left: 4px solid #888;
    padding: 16px 20px;
    border-radius: 6px;
    font-size: 16px;
    color: #333;
    margin-bottom: 24px;
    a {
      color: #1976d2;
      text-decoration: underline;
      cursor: pointer;
    }
    strong {
      display: block;
      margin-bottom: 8px;
    }
  }
</style>
